/**
 * WorkspaceScopeSelector - canonical workspace scope picker.
 *
 * Trigger: same WorkspacePipGlyph as the sidebar icon bar (in-scope / active
 * workspaces only; +N only when that set exceeds PIP_CAP).
 * Popover (multi): click toggles add/remove; Only selects one workspace and closes.
 * Popover (single): click selects exactly one workspace and closes (Secrets, etc.).
 *
 * Used in: Flows filter row, Inbox Kind row, ChatScopeBadge (composer),
 * advanced search, command palette, Secrets settings, Drive account focus (single + candidateIds).
 */

import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { CappedWorkspacePips, WorkspacePipGlyph } from '@/components/layout/workspace-pip-glyph'
import { ShowOverflowLink } from '@/components/ui/show-overflow-link'
import { PIP_CAP, useWorkspaceRecency } from '@/hooks/use-workspace-recency'
import { useWorkspace, type Workspace } from '@/providers/workspace-provider'
import { abbreviateWorkspaceName } from '@/lib/workspace-path-match'
import { getWorkspaceIcon } from '@/lib/workspace-icons'
import {
  LIST_OVERFLOW_PRIMARY_CAP,
  filterSidebarPickerWorkspaces,
  formatShowMoreWorkspacesLabel,
  orderWorkspacesForPicker,
  partitionListOverflow,
} from '@/lib/list-overflow'
import { Building2 } from 'lucide-react'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

function hexToRgba(hex: string, alpha: number): string {
  const raw = hex.replace('#', '')
  if (raw.length !== 6 || Number.isNaN(parseInt(raw, 16))) {
    return `rgba(136, 136, 136, ${alpha})`
  }
  const r = parseInt(raw.slice(0, 2), 16)
  const g = parseInt(raw.slice(2, 4), 16)
  const b = parseInt(raw.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome'

export interface WorkspaceScopeSelectorProps {
  /** Active workspace IDs for this context; null = following fallback or sidebar */
  scope: string[] | null
  onScopeChange: (scope: string[] | null) => void
  /**
   * When scope is null, use this before activeWorkspaceIds (e.g. per-conversation chat scope).
   */
  fallbackScope?: string[] | null
  activeDocumentPath?: string | null
  /** Popover title (default: Chat scope) */
  label?: string
  /** Popover helper text under the title */
  description?: string
  /** Tooltip string for the trigger button */
  tooltip?: string
  /** Extra class names on the trigger button */
  triggerClassName?: string
  /** Extra class names on the selected-workspace name (composer hides this when skinny). */
  nameClassName?: string
  /**
   * multi (default): additive toggle + Only, like chat/search.
   * single: exclusive pick (one workspace); used for Secrets and Drive account focus.
   */
  mode?: 'multi' | 'single'
  /**
   * Limit the menu to these workspace ids (PIP order). Omit to list every org.
   * Drive uses the active set so this is not a second global workspace picker.
   */
  candidateIds?: string[]
  /** Optional test id on the pip trigger button. */
  triggerTestId?: string
}

/** Resolve workspace ID that contains a given file path (contentRoot prefix match). */
function workspaceIdForPath(path: string, workspaces: Workspace[]): string | null {
  if (!path) return null
  const normalized = path.startsWith('/') ? path.slice(1) : path
  for (const w of workspaces) {
    if (w.contentRoot && normalized.startsWith(w.contentRoot.replace(/\/+$/, ''))) return w.id
  }
  return null
}

/** @deprecated Use CappedWorkspacePips from workspace-pip-glyph.tsx */
export function WorkspacePips({
  workspaces,
  activeIds,
  interactive = false,
  className,
}: {
  workspaces: Workspace[]
  activeIds: string[]
  interactive?: boolean
  className?: string
}) {
  return (
    <CappedWorkspacePips
      workspaces={workspaces}
      highlightedIds={activeIds}
      layout="inline"
      interactive={interactive}
      className={className}
    />
  )
}

/** Full-width toggle row for one workspace in the dropdown panel.
 * Active: color rail + wash + full-contrast name. Inactive: faded icon/label. */
export function WorkspaceToggleChip({
  workspace,
  active,
  onToggle,
  onOnly,
  selectionMode = 'additive',
  children,
}: {
  workspace: Workspace
  active: boolean
  onToggle: (multi: boolean) => void
  onOnly?: () => void
  /** additive: click toggles in/out (sidebar + chat/search norm); radio: legacy Cmd/Ctrl multi */
  selectionMode?: 'additive' | 'radio'
  children?: ReactNode
}) {
  const color = workspace.color ?? '#888'
  const Icon = getWorkspaceIcon(workspace.icon, Building2)
  const rowStyle: CSSProperties = active
    ? {
        ['--ws-color' as string]: color,
        borderColor: color,
        color: 'hsl(var(--foreground))',
        backgroundColor: hexToRgba(color, 0.24),
        boxShadow: `inset 3px 0 0 ${color}`,
      }
    : {
        ['--ws-color' as string]: color,
        borderColor: 'hsl(var(--border))',
        color: 'hsl(var(--muted-foreground))',
      }
  return (
    <div
      className={cn(
        'flex w-full items-center gap-1 rounded-md px-1 py-0.5 text-xs font-medium',
        'border focus-within:outline-none focus-within:ring-1',
        'transition-[opacity,background-color,border-color,box-shadow,color] duration-200 ease-in-out',
        !active &&
          'opacity-50 hover:opacity-100 hover:border-[color-mix(in_srgb,var(--ws-color)_50%,transparent)] hover:bg-[color-mix(in_srgb,var(--ws-color)_16%,transparent)] hover:text-foreground hover:shadow-[inset_3px_0_0_var(--ws-color)]',
      )}
      style={rowStyle}
    >
      <button
        type="button"
        onClick={(e) => {
          if (selectionMode === 'additive') {
            onToggle(false)
            return
          }
          onToggle(e.metaKey || e.ctrlKey)
        }}
        aria-pressed={active}
        className="flex min-w-0 flex-1 items-center gap-2 rounded-sm px-1 py-1 text-left bg-transparent border-0 cursor-pointer"
      >
        <span
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-white"
          style={{ backgroundColor: color }}
        >
          <Icon className="h-3 w-3" />
        </span>
        <span className={cn('flex-1 truncate', active && 'font-semibold')}>{workspace.name}</span>
        <span
          className="shrink-0 rounded px-1 py-px text-[10px] font-semibold leading-none border"
          style={
            active
              ? { borderColor: color, color: 'hsl(var(--foreground) / 0.92)' }
              : {
                  borderColor: 'hsl(var(--border))',
                  color: 'hsl(var(--muted-foreground))',
                }
          }
        >
          {abbreviateWorkspaceName(workspace.name)}
        </span>
      </button>
      {onOnly ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onOnly()
          }}
          className="shrink-0 rounded px-1.5 py-1 text-[10px] font-medium leading-none text-foreground/70 hover:bg-accent hover:text-accent-foreground cursor-pointer"
        >
          Only
        </button>
      ) : null}
      {children}
    </div>
  )
}

const DEFAULT_SCOPE_LABEL = 'Chat scope'
const DEFAULT_SCOPE_DESCRIPTION =
  'Limits search, document tools, and product board (getRoadmap). Include Maater Product or reset for the default board.'

export function WorkspaceScopeSelector({
  scope,
  onScopeChange,
  fallbackScope = null,
  activeDocumentPath: _activeDocumentPath,
  label = DEFAULT_SCOPE_LABEL,
  description = DEFAULT_SCOPE_DESCRIPTION,
  tooltip,
  triggerClassName,
  nameClassName,
  mode = 'multi',
  candidateIds,
  triggerTestId,
}: WorkspaceScopeSelectorProps) {
  const { workspaces, activeWorkspaceIds } = useWorkspace()
  const [open, setOpen] = useState(false)
  const [listExpanded, setListExpanded] = useState(false)
  /** Snapshot before Select all; undefined = not in select-all toggle mode. */
  const [preSelectAllScope, setPreSelectAllScope] = useState<string[] | null | undefined>(
    undefined,
  )

  const baselineScope = fallbackScope ?? activeWorkspaceIds
  const effectiveScope = scope ?? baselineScope
  const isFollowingBaseline = scope === null
  const isSingle = mode === 'single'

  const pickerWorkspaces = useMemo(() => {
    if (candidateIds) {
      const byId = new Map(workspaces.map((workspace) => [workspace.id, workspace]))
      return candidateIds
        .map((id) => byId.get(id))
        .filter((workspace): workspace is Workspace => workspace != null)
    }
    return filterSidebarPickerWorkspaces(workspaces, effectiveScope)
  }, [workspaces, candidateIds, effectiveScope])

  const allWorkspaceIds = useMemo(
    () => pickerWorkspaces.map((w) => w.id),
    [pickerWorkspaces],
  )
  // Recency map covers all workspaces (touch when picking from the full menu).
  // Glyph pips match the sidebar: only the in-scope set, not every org (+N overflow).
  const allOrgIds = useMemo(() => workspaces.map((w) => w.id), [workspaces])
  const { touchWorkspace, orderIdsByRecency } = useWorkspaceRecency(allOrgIds)

  const isAllSelected =
    allWorkspaceIds.length > 0 &&
    allWorkspaceIds.every((id) => effectiveScope.includes(id))
  const selectAllToggleActive = preSelectAllScope !== undefined && isAllSelected

  // Manual row toggles after Select all clear the restore snapshot.
  useEffect(() => {
    if (preSelectAllScope === undefined) return
    if (!isAllSelected) setPreSelectAllScope(undefined)
  }, [isAllSelected, preSelectAllScope])

  useEffect(() => {
    if (!open) setListExpanded(false)
  }, [open])

  const pipDisplay = useMemo(() => {
    const ordered = orderIdsByRecency(effectiveScope)
    return {
      visibleIds: ordered.slice(0, PIP_CAP),
      overflow: Math.max(0, ordered.length - PIP_CAP),
    }
  }, [effectiveScope, orderIdsByRecency])

  /**
   * multi: click toggles add/remove; Only selects one and closes.
   * single: click selects exactly that workspace and closes (never empty).
   */
  const handleToggle = (workspaceId: string) => {
    touchWorkspace(workspaceId)
    if (isSingle) {
      onScopeChange([workspaceId])
      setOpen(false)
      return
    }
    const current = scope ?? baselineScope
    const next = current.includes(workspaceId)
      ? current.filter((id) => id !== workspaceId)
      : [...current, workspaceId]
    const isAllActive =
      next.length === baselineScope.length &&
      baselineScope.every((id) => next.includes(id))
    onScopeChange(isAllActive || next.length === 0 ? null : next)
  }

  const handleReset = () => {
    setPreSelectAllScope(undefined)
    if (isSingle) {
      const fallbackId =
        candidateIds?.[0] ?? baselineScope[0] ?? activeWorkspaceIds[0] ?? workspaces[0]?.id
      if (fallbackId) onScopeChange([fallbackId])
      return
    }
    onScopeChange(null)
  }

  /** Select every workspace; second click restores the pre-select-all snapshot. */
  const handleSelectAllToggle = () => {
    if (isSingle || allWorkspaceIds.length === 0) return
    if (selectAllToggleActive) {
      onScopeChange(preSelectAllScope ?? null)
      setPreSelectAllScope(undefined)
      return
    }
    setPreSelectAllScope(scope)
    onScopeChange([...allWorkspaceIds])
  }

  const searchableWorkspaces = useMemo(
    () => orderWorkspacesForPicker(pickerWorkspaces, effectiveScope, orderIdsByRecency),
    [pickerWorkspaces, effectiveScope, orderIdsByRecency],
  )

  const listPartition = useMemo(() => {
    const activeInList = searchableWorkspaces.filter((w) =>
      effectiveScope.includes(w.id),
    ).length
    return partitionListOverflow(searchableWorkspaces, {
      expanded: listExpanded,
      cap: Math.max(LIST_OVERFLOW_PRIMARY_CAP, activeInList),
    })
  }, [searchableWorkspaces, listExpanded, effectiveScope])

  if (searchableWorkspaces.length === 0) return null

  const selectedName =
    isSingle && effectiveScope[0]
      ? workspaces.find((w) => w.id === effectiveScope[0])?.name
      : null

  const tooltipText =
    tooltip ??
    (isSingle
      ? selectedName
        ? `Selected: ${selectedName}`
        : 'Select a workspace'
      : isFollowingBaseline
        ? 'All active workspaces - click to narrow scope'
        : `Scoped to ${effectiveScope.length} workspace${effectiveScope.length !== 1 ? 's' : ''} - click to change`)

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={isSingle ? 'Select workspace' : 'Workspace scope'}
          title={tooltipText}
          data-testid={triggerTestId}
          className={cn(
            'group/pips flex h-7 items-center gap-1.5 rounded-full border px-2 transition-all',
            'text-muted-foreground hover:text-foreground',
            isSingle || !isFollowingBaseline
              ? 'border-border bg-accent/30'
              : 'border-transparent hover:border-border',
            triggerClassName,
          )}
        >
          <WorkspacePipGlyph
            workspaces={workspaces}
            visibleIds={pipDisplay.visibleIds}
            overflow={pipDisplay.overflow}
            layout="inline"
            interactive
          />
          {isSingle && selectedName ? (
            <span className={cn('max-w-[9rem] truncate text-xs text-foreground', nameClassName)}>
              {selectedName}
            </span>
          ) : null}
          <svg
            className="h-3 w-3 shrink-0 opacity-50"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="mb-1.5 px-1.5">
          <p className="text-[10px] font-medium uppercase tracking-wide text-foreground/85">
            {label}
          </p>
          <p className="text-[10px] text-muted-foreground">
            {isSingle
              ? 'Click to select one workspace'
              : 'Click to add or remove; Only for one workspace'}
          </p>
          {description ? (
            <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground">
              {description}
            </p>
          ) : null}
        </div>
        {!isSingle && allWorkspaceIds.length > 1 ? (
          <button
            type="button"
            onClick={handleSelectAllToggle}
            aria-pressed={selectAllToggleActive}
            title={
              selectAllToggleActive
                ? 'Restore the workspace selection from before Select all'
                : 'Select every workspace'
            }
            data-testid={testIds.workspaceScopeSelector.selectAll}
            className={cn(
              'mb-1.5 w-full rounded px-2 py-1 text-center text-[11px] transition-colors',
              selectAllToggleActive
                ? 'bg-accent text-accent-foreground'
                : 'text-foreground/80 hover:bg-accent hover:text-accent-foreground',
            )}
          >
            {selectAllToggleActive ? 'Restore previous' : 'Select all'}
          </button>
        ) : null}
        <div className="flex flex-col gap-1">
          {listPartition.primary.map((w) => {
            const active = effectiveScope.includes(w.id)
            return (
              <WorkspaceToggleChip
                key={w.id}
                workspace={w}
                active={active}
                selectionMode={isSingle ? 'radio' : 'additive'}
                onToggle={() => {
                  setPreSelectAllScope(undefined)
                  handleToggle(w.id)
                }}
                onOnly={
                  isSingle
                    ? undefined
                    : () => {
                        setPreSelectAllScope(undefined)
                        touchWorkspace(w.id)
                        onScopeChange([w.id])
                        setOpen(false)
                      }
                }
              />
            )
          })}
          <ShowOverflowLink
            expanded={listExpanded}
            overflowCount={listPartition.overflowCount}
            onToggle={() => setListExpanded((v) => !v)}
            showMoreLabel={formatShowMoreWorkspacesLabel(listPartition.overflowCount)}
            data-testid={testIds.workspaceScopeSelector.showMore}
          />
        </div>
        {(isSingle ? effectiveScope[0] !== baselineScope[0] : !isFollowingBaseline) && (
          <button
            type="button"
            onClick={handleReset}
            className="mt-1.5 w-full rounded px-2 py-1 text-center text-[11px] text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            {isSingle ? 'Reset to active workspace' : 'Reset to sidebar default'}
          </button>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
